import { useState, useMemo, useEffect } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown, Search, X, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 15;

/**
 * Reusable sort + search hook. Returns filtered+sorted array + props for the
 * stable module-level SortBar component. Consumers render `<SortBar {...sortBarProps} />`.
 *
 * IMPORTANT: SortBar MUST be defined at module scope (not inside the hook) so
 * React preserves its component identity across renders. If it's redefined
 * inline every call, the <input> is unmounted+remounted on every keystroke,
 * which drops focus and closes the mobile keyboard.
 *
 * @param {Array} items
 * @param {Array} sortableFields - [{ key, label }]
 * @param {String} defaultField - key to default-sort by, "" means "Date Added" (uses created_at)
 * @param {Object} resolveMaps - { artistMap: {id: name}, releaseMap: {id: title} } optional
 *   so master search also matches artist names / release titles even though the
 *   entry stores only their IDs.
 */
export function useSortedList(items, sortableFields, defaultField = "", resolveMaps = {}) {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState("desc");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase().trim();
    const { artistMap = {}, releaseMap = {} } = resolveMaps;
    // Recursively flatten every value in the entry into a big searchable string.
    // Also inject resolved artist names & release titles so master-search finds
    // entries by human-readable names, not just their opaque UUIDs.
    const flatten = (obj) => {
      if (obj == null) return "";
      if (Array.isArray(obj)) return obj.map(flatten).join(" ");
      if (typeof obj === "object") return Object.values(obj).map(flatten).join(" ");
      const raw = String(obj).toLowerCase();
      // If this string happens to be an ID in the resolve maps, also append the display name
      const resolved = artistMap[obj] || releaseMap[obj];
      return resolved ? `${raw} ${String(resolved).toLowerCase()}` : raw;
    };
    return items.filter(it => flatten(it).includes(q));
  }, [items, query, resolveMaps]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const valueOf = (it, key) => {
      if (!key) return it.created_at ? new Date(it.created_at).getTime() : 0;
      const v = it?.data ? it.data[key] : it[key];
      if (v == null) return "";
      if (Array.isArray(v)) return v.join(", ");
      if (typeof v === "number") return v;
      const asNum = Number(v);
      if (!Number.isNaN(asNum) && String(v).trim() !== "") return asNum;
      return String(v).toLowerCase();
    };
    copy.sort((a, b) => {
      const av = valueOf(a, sortField);
      const bv = valueOf(b, sortField);
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortField, sortDir]);

  const sortBarProps = { query, setQuery, sortField, setSortField, sortDir, setSortDir, sortableFields };

  // Pagination — 15 per page. Reset to page 1 whenever the filtered set changes
  // (search / sort / underlying items) so the user never lands on an empty page.
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [query, sortField, sortDir, items.length]);
  const paged = useMemo(
    () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sorted, page]
  );
  const paginationProps = { page, totalPages, setPage, total: sorted.length, pageSize: PAGE_SIZE };

  return { sorted: paged, allSorted: sorted, SortBar, sortBarProps, Pagination, paginationProps, sortField, sortDir, query, setQuery };
}

// Module-level pagination bar. Only renders if there is more than one page.
export function Pagination({ page, totalPages, setPage, total, pageSize }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  // Compact page list: always show 1, current-1, current, current+1, last. Ellipse the rest.
  const nums = new Set([1, totalPages, page - 1, page, page + 1].filter(n => n >= 1 && n <= totalPages));
  const list = Array.from(nums).sort((a, b) => a - b);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10 }} data-testid="pagination">
      <span className="muted" style={{ fontSize: 11 }}>Showing {from}–{to} of {total}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev" style={{ padding: "5px 8px", opacity: page <= 1 ? 0.4 : 1 }}>
          <ChevronLeft size={13} />
        </button>
        {list.map((n, i) => {
          const prev = list[i - 1];
          return (
            <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {prev && n - prev > 1 && <span className="muted" style={{ padding: "0 4px", fontSize: 12 }}>…</span>}
              <button
                onClick={() => setPage(n)}
                data-testid={`page-${n}`}
                className={"btn " + (n === page ? "btn-primary" : "")}
                style={{ padding: "5px 10px", fontSize: 12, minWidth: 30, fontFamily: "Space Grotesk", fontWeight: 600 }}
              >
                {n}
              </button>
            </span>
          );
        })}
        <button className="btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next" style={{ padding: "5px 8px", opacity: page >= totalPages ? 0.4 : 1 }}>
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

// Module-level component so React keeps a stable identity between renders.
// This is what makes the search input maintain focus and the mobile keyboard stay open.
export function SortBar({ query, setQuery, sortField, setSortField, sortDir, setSortDir, sortableFields }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", width: "100%" }}>
      <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 380, minWidth: 180 }}>
        <Search size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted)", pointerEvents: "none" }} />
        <input
          className="input"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="search-input"
          style={{ padding: "8px 30px 8px 32px", fontSize: 13, width: "100%" }}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            data-testid="search-clear"
            title="Clear"
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4 }}
          >
            <X size={13} />
          </button>
        )}
      </div>
      <span className="muted" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 5 }}>
        <ArrowUpDown size={11} /> Sort
      </span>
      <select
        className="input"
        style={{ maxWidth: 200, padding: "6px 10px", fontSize: 12, flex: "0 1 auto" }}
        value={sortField}
        onChange={(e) => setSortField(e.target.value)}
        data-testid="sort-field"
      >
        <option value="">Date Added</option>
        {sortableFields.map((f) => (
          <option key={f.key} value={f.key}>{f.label}</option>
        ))}
      </select>
      <button
        type="button"
        className="btn"
        onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
        title={sortDir === "asc" ? "Ascending — click for Descending" : "Descending — click for Ascending"}
        style={{ padding: "6px 10px", color: "var(--mint)" }}
        data-testid="sort-dir"
      >
        {sortDir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
      </button>
    </div>
  );
}
